import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'leaflet/dist/leaflet.css';
import './EventItem.css';
import { get_event, get_event_illustration, get_event_document, get_user_info } from '../../api/functions';
import { useApi } from '../../api/api';
import { useAuth } from '../../provider/auth';
import { DocumentViewer, LoadingSpinner } from '../../components';
import { ErrorScreen } from '../';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const EventItem = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const getUserInfoApi = useApi(get_user_info);
  const getEventApi = useApi(get_event, 300);
  const getEventImage = useApi(get_event_illustration);
  const getEventDocument = useApi(get_event_document);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [docUrl, setDocUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [isPdf, setIsPdf] = useState(false);
  const [eventItem, setEventItem] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      await getUserInfoApi.execute();
      setUserLoaded(true);
    };
    loadUser();
  }, [token]);

  useEffect(() => {
    if (!userLoaded) return;

    const fetchData = async () => {
      setIsLoading(true);
      setAuthError(false);

      try {
        const eventInfo = await getEventApi.execute({ event_id: id });

        if (!eventInfo) {
          setIsLoading(false);
          return;
        }

        const startDate = new Date(`${eventInfo.event_start_date}T${eventInfo.event_start_time}`);
        const endDate = new Date(`${eventInfo.event_end_date}T${eventInfo.event_end_time}`);
        const isMultiDay = eventInfo.event_start_date !== eventInfo.event_end_date;

        const langs = {
          'en': 'en-GB',
          'de': 'de-DE',
          'cs': 'cs-CZ'
        };
        const locale = langs[i18n.language];
        const loc = i18n.language === 'cs' ? 'cz' : i18n.language;

        // Use coordinates from database (geocoded when event was created/updated)
        const coords = (eventInfo.location_latitude && eventInfo.location_longitude)
          ? { lat: eventInfo.location_latitude, lng: eventInfo.location_longitude }
          : null;

        setEventItem({
          id: eventInfo.id,
          day: String(startDate.getDate()).padStart(2, '0'),
          month: startDate.toLocaleDateString(locale, { month: "long" })
            .replace(/^./, c => c.toUpperCase()),
          year: startDate.toLocaleDateString(locale, { year: "numeric" }),
          startDate: startDate.toLocaleDateString(locale),
          endDate: endDate.toLocaleDateString(locale),
          isMultiDay: isMultiDay,
          title: eventInfo[`title_${loc}`] || eventInfo.title_cz || eventInfo.title_en || eventInfo.title_de || 'Untitled',
          description: eventInfo[`text_${loc}`] || eventInfo.text_cz || eventInfo.text_en || eventInfo.text_de || '',
          startTime: startDate.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
          endTime: endDate.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
          locationName: eventInfo.location_name,
          address: eventInfo.location_address,
          membersOnly: eventInfo.private,
          coordinates: coords,
          illustrationFilename: eventInfo.illustration_filename,
          documentFilename: eventInfo.document_filename
        });
        console.log(eventItem);
        if (eventInfo.illustration_filename) {
          getEventImage.execute({ event_id: id });
        } else {
          setLoadingImage(false);
        }

        if (eventInfo.document_filename) {
          getEventDocument.execute({ event_id: id });
        } else {
          setLoadingDoc(false);
        }
      } catch (error) {
        if (error.message?.includes('401') || error.message?.includes('403')) {
          setAuthError(true);
        }
        console.error('Error loading event:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, i18n.language, userLoaded, token]);

  useEffect(() => {
    if (getEventImage.data instanceof Blob && !getEventImage.error) {
      const url = URL.createObjectURL(getEventImage.data);
      setImageUrl(url);
      setLoadingImage(false);
      return () => URL.revokeObjectURL(url);
    } else if (getEventImage.error) {
      setImageUrl(null);
      setLoadingImage(false);
    }
  }, [getEventImage.data, getEventImage.error]);

  useEffect(() => {
    if (getEventDocument.data instanceof Blob && !getEventDocument.error) {
      const url = URL.createObjectURL(getEventDocument.data);
      setDocUrl(url);
      setIsPdf(getEventDocument.data.type === 'application/pdf');
      setLoadingDoc(false);
      return () => URL.revokeObjectURL(url);
    } else if (getEventDocument.error) {
      setDocUrl(null);
      setLoadingDoc(false);
    }
  }, [getEventDocument.data, getEventDocument.error]);

  if (!userLoaded || isLoading) {
    return (
      <div className="event-item-page">
        <div className="event-item-page-loading">
          <LoadingSpinner message={t('events.loading')} />
        </div>
      </div>
    );
  }

  if (authError) {
    return <ErrorScreen error={401} />;
  }

  if (!eventItem.id) {
    return <ErrorScreen error={404} />;
  }

  const hasImages = imageUrl !== null;
  const hasMultipleImages = false;

  const mapContainerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '12px',
    zIndex: 1
  };

  return (
    <div className="event-item-page">
      <main className="event-item-page-main">
        <div className="event-item-container">
          <Link to="/events" className="event-item-back-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('events.backToEvents')}
          </Link>

          <header className="event-item-header">
            {eventItem.day && (
              <div className="event-item-date-badge">
                <div className="event-date-day">{eventItem.day}</div>
                <div className="event-date-month">{eventItem.month}</div>
              </div>
            )}
            <div className="event-item-header-content">
              {eventItem.title && (
                <h1 className="event-item-title">{eventItem.title}</h1>
              )}
              {eventItem.membersOnly && (
                <span className="event-item-badge">{t('events.membersOnly')}</span>
              )}
            </div>
          </header>

          {hasImages && (
            <div className="event-item-media">
              {hasMultipleImages ? (
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                  }}
                  loop={true}
                  className="event-item-carousel"
                >
                </Swiper>
              ) : (
                <div className="event-item-single-image">
                  <img src={imageUrl} alt={eventItem.title} />
                </div>
              )}
            </div>
          )}

          <div className="event-item-info-grid">
            {(eventItem.startDate || eventItem.startTime) && (
              <div className="event-item-info-card">
                <div className="event-item-info-content">
                  <div className="event-item-info-header">
                    <div className="event-item-info-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                        <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <h3 className="event-item-info-title">{t('events.when')}</h3>
                  </div>
                  <div className="event-item-info-detail">
                    {eventItem.isMultiDay ? (
                      <>
                        <div className="event-item-date-text">{t('events.date')}: {eventItem.startDate} - {eventItem.endDate}</div>
                      </>
                    ) : (
                      <div className="event-item-date-text">{t('events.date')}: {eventItem.startDate}</div>
                    )}
                    <div className="event-item-time">
                      {t('events.time')}: {eventItem.startTime} - {eventItem.endTime}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {(eventItem.locationName || eventItem.address) && (
              <div className="event-item-info-card">
                <div className="event-item-info-content">
                  <div className="event-item-info-header">
                    <div className="event-item-info-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" />
                        <circle cx="12" cy="10" r="3" strokeWidth="2" />
                      </svg>
                    </div>
                    <h3 className="event-item-info-title">{t('events.where')}</h3>
                  </div>
                  <div className="event-item-info-detail">
                    {eventItem.locationName && (
                      <div className="event-item-location-name">{t('events.locationLabel')}: {eventItem.locationName}</div>
                    )}
                    {eventItem.address && (
                      <div className="event-item-address-text">{t('events.addressLabel')}: {eventItem.address}</div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>

          {eventItem.description && (
            <div className="event-item-description">
              <h3 className="event-item-section-title">{t('events.description')}</h3>
              <div className="event-item-text-section">
                <div className="event-item-description-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {eventItem.description}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}

          {(eventItem.coordinates || eventItem.address) && (
            <div className="event-item-map-section">
              <h3 className="event-item-section-title">{t('events.location')}</h3>

              {eventItem.coordinates ? (
                <div className="event-item-map-wrapper">
                  <MapContainer
                    center={[eventItem.coordinates.lat, eventItem.coordinates.lng]}
                    zoom={15}
                    style={mapContainerStyle}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[eventItem.coordinates.lat, eventItem.coordinates.lng]}>
                      <Popup>{eventItem.title}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              ) : eventItem.address ? (
                <div className="event-item-map-placeholder">
                  <div className="event-item-map-placeholder-content">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" />
                      <circle cx="12" cy="10" r="3" strokeWidth="2" />
                    </svg>
                    <div className="event-item-map-placeholder-info">
                      <div className="event-item-map-placeholder-address">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <p className="event-item-map-placeholder-title">{eventItem.address}</p>
                      </div>
                      <p className="event-item-map-placeholder-text">
                        {t('events.mapCoordinatesNotAvailable', 'Unable to locate address on map')}
                      </p>
                    </div>
                    <a
                      href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(eventItem.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-item-map-link"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {t('events.openInMaps')}
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {eventItem.documentFilename && docUrl && (
            <DocumentViewer docUrl={docUrl} isPdf={isPdf} />
          )}
        </div>
      </main>
    </div>
  );
};

export default EventItem;
